import React, { useEffect, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../App';
import api, { clearStoredSession, isAuthError, refreshCsrfToken } from '../utils/api';
import { getOAuthRedirectUri } from '../utils/oauthRedirectOrigin';
import { applyExchangeAccessToken } from '../utils/exchangeAuth';

function mapMeToUser(d) {
  return {
    id: d.id,
    email: d.email,
    name: d.name,
    role: d.role,
    type: d.activeContext,
    availableContexts: d.availableContexts || null,
    nomineeEstates: d.nomineeEstates || [],
    activeOwnerSub: d.activeOwnerSub || null,
    marketingConsent: d.marketingConsent,
    marketingConsentAt: d.marketingConsentAt,
    marketingConsentVersion: d.marketingConsentVersion,
    isAdmin: !!d.isAdmin,
  };
}

async function tryNavigateFromApiSession(navigate, setCurrentUser) {
  try {
    await refreshCsrfToken();
    const me = await api.get('/auth/me');
    const enriched = mapMeToUser(me.data);
    setCurrentUser(enriched);
    try {
      window.localStorage.setItem('currentUser', JSON.stringify(enriched));
    } catch {}

    const isDual = enriched.role === 'dual';
    const dualChosen = (() => {
      try {
        return window.localStorage.getItem('dual_context_chosen_v1') === '1';
      } catch {
        return false;
      }
    })();
    if (isDual && !dualChosen) {
      navigate('/choose-context');
      return true;
    }
    if (enriched.role === 'nominee' || enriched.type === 'nominee') {
      navigate('/nominee');
      return true;
    }
    navigate('/dashboard');
    return true;
  } catch {
    return false;
  }
}

function exchangeFailStorageKey(authCode) {
  return `pv_oauth_ex_failmsg:${authCode}`;
}

function writeExchangeFailMessage(authCode, message) {
  try {
    if (!authCode) return;
    sessionStorage.setItem(exchangeFailStorageKey(authCode), String(message || '').slice(0, 1500));
  } catch (_) {}
}

function clearExchangeFailMessage(authCode) {
  try {
    if (!authCode) return;
    sessionStorage.removeItem(exchangeFailStorageKey(authCode));
  } catch (_) {}
}

function readExchangeFailMessage(authCode) {
  try {
    const v = sessionStorage.getItem(exchangeFailStorageKey(authCode));
    return v ? String(v) : '';
  } catch {
    return '';
  }
}

/**
 * Strict Mode waiter: wait for sibling's exchange to attach cookies — but stop
 * immediately if sibling records `writeExchangeFailMessage` (exchange 400/502/500 etc.).
 *
 * Prefer checking failure + `/auth/me` BEFORE sleeping so we do not blindly
 * hammer the API once we know Cognito redeemed the code but session persist failed.
 */
async function pollForSession(navigate, setCurrentUser, deadlineMs, stepMs, authCodeFailWatch = null) {
  const trimmedWatch = typeof authCodeFailWatch === 'string' ? authCodeFailWatch.trim() : '';
  const deadline = Date.now() + deadlineMs;
  while (Date.now() < deadline) {
    if (trimmedWatch) {
      const failEarly = readExchangeFailMessage(trimmedWatch);
      if (failEarly) {
        clearExchangeFailMessage(trimmedWatch);
        return { status: 'exchange_failed', message: failEarly };
      }
    }
    // eslint-disable-next-line no-await-in-loop
    if (await tryNavigateFromApiSession(navigate, setCurrentUser)) return { status: 'ok' };
    if (Date.now() >= deadline) break;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, stepMs));
  }
  return { status: 'timeout' };
}

/**
 * Busy key value is a Unix ms timestamp written synchronously BEFORE the token
 * exchange awaits. React 18 Strict Mode runs two effect passes; only the leader
 * should POST /auth/exchange (codes are single-use). The waiter polls until cookies exist.
 *
 * IMPORTANT: Never clear this key from useEffect cleanup while the POST is still
 * in flight — that would let another pass POST again and Cognito responds invalid_grant.
 */
const EX_BUSY_PREFIX = 'pv_oauth_ex_busy:';

function exchangeBusyMsKey(code) {
  return `${EX_BUSY_PREFIX}${code}`;
}

/** If Leader crashes before clearing the busy stamp, reclaim after this window. */
const EXCHANGE_HOLD_MS = 90_000;

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setCurrentUser } = useContext(UserContext);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const redirectUri = getOAuthRedirectUri();
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      if (!code) {
        setError('Missing OAuth parameters');
        return;
      }

      /** User refreshed `/auth/callback?code=` after OAuth already finished */
      const completedKey = `auth_callback_exchange_done:${code}`;
      if (typeof window !== 'undefined' && sessionStorage.getItem(completedKey) === '1') {
        if (cancelled) return;
        const okImmediate = await tryNavigateFromApiSession(navigate, setCurrentUser);
        if (cancelled) return;
        if (okImmediate) return;
        const okPoll = await pollForSession(navigate, setCurrentUser, 4500, 280, code);
        if (cancelled) return;
        if (okPoll.status === 'exchange_failed') {
          setError(okPoll.message);
          return;
        }
        if (okPoll.status !== 'ok') {
          setError('Login could not be completed. Please retry sign-in.');
        }
        return;
      }

      const busyKey = exchangeBusyMsKey(code);
      let leader = false;
      try {
        const now = Date.now();
        const stamp = sessionStorage.getItem(busyKey);
        if (!stamp) {
          sessionStorage.setItem(busyKey, String(now));
          leader = true;
        } else {
          const t = Number(stamp);
          const stale = !Number.isFinite(t) || now - t > EXCHANGE_HOLD_MS;
          if (stale) {
            sessionStorage.setItem(busyKey, String(now));
            leader = true;
          }
        }
      } catch (_) {
        leader = true;
      }

      /**
       * Strict Mode waiter: leader has the busy stamp and is doing the POST /auth/exchange.
       * Wait passively for the leader to either:
       *  - finish (busyKey removed AND completedKey set), then poll once for session, or
       *  - record an exchange failure message.
       * Do NOT hit /auth/me while waiting — leader hasn't attached a session yet, so
       * every poll would just generate a 401.
       */
      if (!leader) {
        const followerDeadline = Date.now() + 12_000;
        while (Date.now() < followerDeadline) {
          if (cancelled) return;
          const failMsg = readExchangeFailMessage(code);
          if (failMsg) {
            clearExchangeFailMessage(code);
            setError(failMsg);
            return;
          }
          const stillBusy = sessionStorage.getItem(busyKey);
          const completed = sessionStorage.getItem(completedKey) === '1';
          if (!stillBusy && completed) break;
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 150));
        }
        if (cancelled) return;
        if (!(await tryNavigateFromApiSession(navigate, setCurrentUser))) {
          setError('Login could not be completed. Please retry sign-in.');
        }
        return;
      }

      const codeVerifier = sessionStorage.getItem('pkce_verifier') || '';
      if (!codeVerifier) {
        try {
          sessionStorage.removeItem(busyKey);
        } catch (_) {}
        setError('Missing PKCE verifier. Please retry sign-in.');
        return;
      }

      try {
        if (cancelled) return;
        clearExchangeFailMessage(code);

        const exchangeRes = await api.post('/auth/exchange', {
          code,
          code_verifier: codeVerifier,
          redirect_uri: redirectUri,
        });
        try {
          sessionStorage.setItem(completedKey, '1');
        } catch (_) {}

        // Use access_token from the exchange body when present (session cookie +
        // Bearer backup for cross-origin SPAs where pv_session may not be sent).
        applyExchangeAccessToken(api, exchangeRes?.data);

        await refreshCsrfToken();

        try {
          const inviteToken = sessionStorage.getItem('pending_nominee_invite_token');
          if (inviteToken) {
            await api.post('/auth/nominee-invite/attach', { token: inviteToken });
            sessionStorage.removeItem('pending_nominee_invite_token');
          }
        } catch (e) {
          console.error('Failed to attach nominee invite after login:', e?.response?.data || e?.message);
        }

        try {
          const me = await api.get('/auth/me');
          const enriched = mapMeToUser(me.data);
          setCurrentUser(enriched);
          try {
            localStorage.setItem('currentUser', JSON.stringify(enriched));
          } catch (_) {}

          try {
            window.sessionStorage.removeItem('force_reauth');
          } catch (_) {}

          const isDual = enriched.role === 'dual';
          const dualChosen = (() => {
            try {
              return localStorage.getItem('dual_context_chosen_v1') === '1';
            } catch {
              return false;
            }
          })();
          if (isDual && !dualChosen) {
            navigate('/choose-context');
            return;
          }
          if (enriched.role === 'nominee' || enriched.type === 'nominee') {
            navigate('/nominee');
            return;
          }
          navigate('/dashboard');
          return;
        } catch (err) {
          console.error('AuthCallback: failed to load session via GET /auth/me', err?.response?.data || err);
          if (isAuthError(err)) {
            clearStoredSession();
            setCurrentUser(null);
            setError(
              'Signed in, but the API could not load your profile. Check your API base URL and Cognito configuration, then retry sign-in.',
            );
            return;
          }
        }

        navigate('/dashboard');
      } catch (e) {
        try {
          window.sessionStorage.removeItem('pkce_verifier');
        } catch (_) {}
        if (!cancelled) {
          const cogErr = String(e?.response?.data?.cognito_error || '');
          const detailRaw = String(e?.response?.data?.detail || '');
          const invalidGrant = cogErr === 'invalid_grant' || /invalid_grant/i.test(detailRaw);
          try {
            sessionStorage.removeItem(completedKey);
          } catch (_) {}
          try {
            const u = new URL(window.location.href);
            u.searchParams.delete('code');
            u.searchParams.delete('state');
            window.history.replaceState({}, '', u.pathname + u.search + u.hash);
          } catch (_) {}
          const bd = e?.response?.data || {};
          const sessionHint =
            typeof bd.hint === 'string'
              ? bd.hint.trim()
              : '';
          const sessionDetail =
            typeof bd.detail === 'string'
              ? bd.detail.trim().slice(0, 280)
              : '';
          const sessionReason =
            typeof bd.reason === 'string'
              ? bd.reason.trim()
              : '';
          let msg =
            'Authentication failed. Start sign-in again from the login screen.';
          if (invalidGrant) {
            msg =
              'OAuth sign-in failed (invalid_grant): the authorization code did not redeem—usually redirect_uri mismatch '
              + '(always use http://localhost:3000 or add both localhost and 127.0.0.1 callbacks in Cognito), or '
              + 'CLIENT_ID/backend COGNITO_AUDIENCE mismatch, or retrying after the code expired.';
          } else if (e?.response?.status === 500 && String(bd.error || '').includes('Failed to establish session')) {
            const detailBits = [
              sessionHint ? sessionHint : null,
              sessionDetail ? sessionDetail : null,
              sessionReason ? `(error.name/code: ${sessionReason})` : null,
            ].filter(Boolean);
            msg =
              'Sign-in succeeded with Cognito, but the API could not create a browser session (you will stay logged out). '
              + 'Usually Aurora cannot be reached locally, inserting into auth_sessions failed, '
              + 'or IAM denied kms:GenerateDataKey for ASSET_KMS_KEY_ID. '
              + (detailBits.length > 0
                ? `Server said: ${detailBits.join(' ')}`
                : 'Check the API process logs for "[auth] session_create_failed".');
          }
          writeExchangeFailMessage(code, msg);
          setError(msg);
        }
      } finally {
        try {
          sessionStorage.removeItem(busyKey);
        } catch (_) {}
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, setCurrentUser]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {error ? <div>{error}</div> : <div>Signing you in…</div>}
    </div>
  );
}
